import React, { useState, useEffect } from "react";
import api from "../../api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaPlusCircle, FaCalendarAlt, FaChalkboardTeacher } from "react-icons/fa";

const ExamManagementPage = () => {
  const [exams, setExams] = useState([]);
  const [form, setForm] = useState({
    exam_name: "",
    exam_type: "",
    class_id: "",
    start_date: "",
    end_date: "",
  });
  const [classes, setClasses] = useState([]);

  useEffect(() => {
    fetchClasses();
    fetchExams();
  }, []);

  const fetchClasses = async () => {
    try {
      const res = await api.get("/get-classes");
      if (res.data?.success) setClasses(res.data.data);
    } catch (err) {
      toast.error("Failed to load classes.");
    }
  };

  const fetchExams = async () => {
    try {
      const res = await api.get("/exam-management/list");
      if (res.data?.status) setExams(res.data.data);
    } catch (err) {
      toast.error("Failed to load exams.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post("/exam-management/add", form);
      if (res.data.status) {
        toast.success("✅ Exam Added Successfully!");
        setForm({ exam_name: "", exam_type: "", start_date: "", end_date: "" });
        fetchExams();
      } else toast.warning(res.data.message || "Something went wrong!");
    } catch {
      toast.error("Failed to add exam.");
    }
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={2500} hideProgressBar />

      <style>{`
        /* ====== Premium Design ====== */
        .exam-container {
          min-height: 100vh;
          background: #f6f9ff;
          font-family: "Poppins", sans-serif;
        }

        /* Curved Gradient Header */
        .exam-header {
          position: relative;
          background: linear-gradient(135deg, #3b82f6, #2563eb, #1e40af);
          color: white;
          padding: 60px 20px 100px;
          text-align: center;
          border-bottom-left-radius: 50% 10%;
          border-bottom-right-radius: 50% 10%;
          box-shadow: 0 8px 25px rgba(59, 130, 246, 0.4);
        }

        .exam-header h1 {
          font-size: 34px;
          font-weight: 800;
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 10px;
        }

        .exam-header p {
          font-size: 15px;
          color: #e0e7ff;
          margin-top: 5px;
        }

        /* Form Card */
        .exam-form-card {
          margin: 10px auto 0;
          background: white;
          border-radius: 20px;
          padding: 30px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
          transition: 0.3s;
        }

        .exam-form {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 18px;
        }

        .exam-form input,
        .exam-form select {
          padding: 12px 14px;
          border: 1px solid #d1d5db;
          border-radius: 10px;
          font-size: 14px;
          transition: 0.3s;
        }

        .exam-form input:focus,
        .exam-form select:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 5px rgba(59, 130, 246, 0.4);
          outline: none;
        }

        .date-input {
          display: flex;
          align-items: center;
          gap: 10px;
          border: 1px solid #d1d5db;
          border-radius: 10px;
          padding: 10px 12px;
        }

        .date-input input {
          border: none;
          outline: none;
          width: 100%;
        }

        .add-btn {
          background: linear-gradient(90deg, #2563eb, #3b82f6);
          color: white;
          font-weight: 600;
          border: none;
          border-radius: 10px;
          cursor: pointer;
          transition: 0.3s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 12px;
          box-shadow: 0 5px 15px rgba(37, 99, 235, 0.4);
        }

        .add-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(37, 99, 235, 0.6);
        }

        /* Table Section */
        .exam-table-card {
          margin: 50px auto;
          background: white;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.07);
        }

        .exam-table {
          width: 100%;
          border-collapse: collapse;
        }

        .exam-table thead {
          background: linear-gradient(90deg, #2563eb, #3b82f6);
          color: white;
        }

        .exam-table th,
        .exam-table td {
          padding: 16px 20px;
          text-align: left;
          font-size: 14px;
        }

        .exam-table tbody tr:nth-child(even) {
          background: #f9fafb;
        }

        .exam-table tbody tr:hover {
          background: #eef2ff;
          transition: 0.3s;
        }

        .no-data {
          text-align: center;
          color: #9ca3af;
          padding: 20px 0;
          font-style: italic;
        }

        @media (max-width: 768px) {
          .exam-header h1 {
            font-size: 24px;
          }
          .exam-form {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <div className="exam-container">
        {/* Header */}
        <div className="exam-header">
          <h1>
            <FaChalkboardTeacher /> Exam Management
          </h1>
          <p>Create and manage examinations for your classes efficiently</p>
        </div>

        {/* Form Section */}
        <div className="exam-form-card">
          <form onSubmit={handleSubmit} className="exam-form">
            <input
              type="text"
              placeholder="Enter Exam Name"
              value={form.exam_name}
              onChange={(e) => setForm({ ...form, exam_name: e.target.value })}
            />
            <select
              value={form.exam_type}
              onChange={(e) => setForm({ ...form, exam_type: e.target.value })}
            >
              <option value="">Exam Type</option>
              <option value="unit_test_1">Unit Test 1</option>
              <option value="unit_test_2">Unit Test 2</option>
              <option value="half_yearly">Half Yearly</option>
              <option value="final">Final Exam</option>
            </select>

            <div className="date-input">
              <FaCalendarAlt color="#2563eb" />
              <input
                type="date"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
              />
            </div>

            <div className="date-input">
              <FaCalendarAlt color="#2563eb" />
              <input
                type="date"
                value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
              />
            </div>

            <button type="submit" className="add-btn">
              <FaPlusCircle /> Add Exam
            </button>
          </form>
        </div>

        {/* Table Section */}
        <div className="exam-table-card">
          <table className="exam-table">
            <thead>
              <tr>
                <th>Exam Name</th>
                <th>Type</th>
                <th>Start Date</th>
                <th>End Date</th>
              </tr>
            </thead>
            <tbody>
              {exams.length > 0 ? (
                exams.map((e, i) => (
                  <tr key={i}>
                    <td>{e.exam_name}</td>
                    <td>{e.exam_type.replace("_", " ")}</td>
                    <td>{e.start_date}</td>
                    <td>{e.end_date}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="no-data">
                    No exams available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default ExamManagementPage;
